<template>
    <!-- Mounted once, in the left menu, and opened by a bus event: its two
         triggers are the account menu (whose content only exists while the menu
         is open) and the checklist toolbar, which is a different screen. -->
    <v-dialog v-model="dialog" max-width="770">
        <v-card :loading="loading ? 'accent' : false">
            <v-card-title class="text-h5 d-flex align-center">
                {{ t("select_employees") }}
                <v-spacer />
                <span v-if="selected.length" class="text-caption text-accent">
                    {{ selected.length }} {{ t("selected") }}
                </span>
            </v-card-title>
            <!-- Says what this set is for. The user form holds a list of people
                 too, and it means something else: who *may* work under the
                 account. This one is who is at the station right now. -->
            <v-card-subtitle class="text-wrap">
                {{ t("select_employees_hint") }}
            </v-card-subtitle>
            <v-card-text>
                <!-- Cards toggle and the dialog stays open: this is a set, and
                     picking the second person must not close the screen. -->
                <div class="d-flex ma-3 flex-wrap">
                    <!-- Same shape for every card: what a card looks like has to
                         mean "selected" and nothing else. Which one is the account
                         itself is said by the icon and the subtitle, not by
                         styling that competes with the selected state. -->
                    <v-card v-for="emp in employees" :key="emp.id" @click="toggle(emp.id)" border
                        class="ma-1 text-center pa-3 position-relative" width="195px"
                        :color="isSelected(emp.id) ? 'accent' : ''">
                        <v-icon size="x-large" :color="isSelected(emp.id) ? '' : 'grey-lighten-1'">
                            {{ emp.id == my_id ? "mdi-key-variant" : "mdi-badge-account-horizontal-outline" }}
                        </v-icon>
                        <div class="text-subtitle-1 font-weight-bold">
                            {{ emp.alias }}
                        </div>
                        <div class="text-caption text-medium-emphasis" style="min-height: 1.2em">
                            {{ emp.id == my_id ? t("this_account") : emp.name }}
                        </div>
                        <div class="text-caption text-disabled" style="min-height: 1.2em">
                            {{ emp.client_id }}
                        </div>
                        <v-icon v-if="isSelected(emp.id)" class="tick" size="small">
                            mdi-check-circle
                        </v-icon>
                    </v-card>
                </div>
            </v-card-text>
            <v-card-actions>
                <!-- Unticking everything is not "nobody": the records fall back
                     to the account itself, unless it took itself off its list. -->
                <v-btn color="accent" variant="text" @click="selected = []" :disabled="!selected.length">
                    {{ t("clean") }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn color="accent" variant="flat" @click="save" :loading="loading" :disabled="loading">
                    <v-icon start> mdi-check </v-icon>
                    {{ t("save") }}
                </v-btn>
                <v-btn color="secondary" variant="text" @click="dialog = false">
                    {{ t("close") }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
.tick {
    position: absolute;
    top: 4px;
    left: 6px;
}
</style>

<script>
import bus from "/src/utils/event_bus";
import { usePitStoreBasics } from "@/stores/app_common";
import { useSessionStore } from "@/stores/session";
import { mapState } from "pinia";

export default {
    setup() {
        return { ...usePitStoreBasics(), session_store: useSessionStore() }
    },
    data: () => ({ dialog: false, loading: false, selected: [] }),
    computed: {
        ...mapState(useSessionStore, [
            "my_employees", "selected_employee_ids", "user_object",
        ]),
        employees() {
            return [...this.my_employees].sort((a, b) => {
                // The account itself first, in the same place it holds in the
                // user table, so the grid reads the same on every screen.
                if (a.id == this.my_id) return -1
                if (b.id == this.my_id) return 1
                return (a.alias || "").localeCompare(b.alias || "")
            })
        },
        my_id() {
            return this.user_object?.id
        },
    },
    methods: {
        isSelected(id) {
            return this.selected.includes(id)
        },
        toggle(id) {
            this.selected = this.isSelected(id)
                ? this.selected.filter((i) => i != id)
                : [...this.selected, id]
        },
        save() {
            this.loading = true
            this.session_store.postSelectedProductionEmployees(this.selected)
                .then(() => (this.dialog = false))
                .catch(() => {
                    this.$bus.emit("notification", {
                        timeout: 4000,
                        text: "Can't save the selection.",
                        color: "error",
                    })
                })
                .finally(() => (this.loading = false))
        },
    },
    watch: {
        dialog(d) {
            // Reset from the store on every open, so a cancelled edit does not
            // leave the dialog disagreeing with the button next to it.
            if (d) this.selected = [...this.selected_employee_ids]
        },
    },
    mounted() {
        bus.on("events/employees/open_selector", () => (this.dialog = true))
    },
    unmounted() {
        bus.off("events/employees/open_selector")
    },
};
</script>
